import React from 'react';
import { Phone, Loader2, WifiOff, ChevronDown, History } from 'lucide-react';
import { CONNECTION_STATES } from './ConnectionStatus';

const DIALPAD_BUTTONS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['*', '0', '#'],
];

/**
 * Idle-state dial pad. Renders the from-number selector, the phone-number
 * input, the keypad, and the green call button.
 */
export const DialPad = ({
  phoneNumber,
  onPhoneNumberChange,
  phoneNumbers,
  selectedFromNumber,
  onSelectedFromNumberChange,
  connectionState,
  loading,
  onCall,
  onToggleHistory,
}) => (
  <>
    <div className="flex items-center justify-between mb-3">
      <span className="text-gray-400 text-xs uppercase tracking-wide">Dial a number</span>
      {onToggleHistory && (
        <button
          onClick={onToggleHistory}
          className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs text-teal-400 hover:bg-teal-400/10 transition-colors"
          data-testid="dialer-history-toggle-btn"
        >
          <History className="w-3.5 h-3.5" />
          <span>Recent</span>
        </button>
      )}
    </div>

    {phoneNumbers.length > 0 && (
      <div className="mb-4">
        <label className="block text-gray-400 text-xs mb-1">Calling from</label>
        <div className="relative">
          <select
            value={selectedFromNumber}
            onChange={(e) => onSelectedFromNumberChange(e.target.value)}
            className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm appearance-none pr-8"
            data-testid="dialer-from-number-select"
          >
            {phoneNumbers.map((num) => (
              <option key={num.number} value={num.number}>
                {num.label}: {num.number}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
      </div>
    )}

    <div className="mb-4">
      <input
        type="tel"
        value={phoneNumber}
        onChange={(e) => onPhoneNumberChange(e.target.value)}
        placeholder="Enter phone number"
        className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-3 text-white text-xl text-center tracking-wider outline-none focus:border-teal-400"
        data-testid="dialer-phone-number-input"
      />
    </div>

    <div className="grid grid-cols-3 gap-2 mb-4">
      {DIALPAD_BUTTONS.flat().map((digit) => (
        <button
          key={digit}
          onClick={() => onPhoneNumberChange(phoneNumber + digit)}
          className="aspect-square bg-[#1a1a1f] hover:bg-[#252530] border border-gray-700 rounded-xl text-white text-xl font-medium transition-colors"
          data-testid={`dialer-keypad-${digit}`}
        >
          {digit}
        </button>
      ))}
    </div>

    <button
      onClick={onCall}
      disabled={loading || !phoneNumber.trim() || connectionState !== CONNECTION_STATES.CONNECTED}
      className="w-full py-4 bg-green-500 hover:bg-green-600 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-xl flex items-center justify-center gap-2 text-white font-semibold transition-colors"
      data-testid="call-button"
    >
      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin" />
      ) : connectionState !== CONNECTION_STATES.CONNECTED ? (
        <>
          <WifiOff className="w-6 h-6" />
          Not Connected
        </>
      ) : (
        <>
          <Phone className="w-6 h-6" />
          Call
        </>
      )}
    </button>

    {connectionState !== CONNECTION_STATES.CONNECTED && (
      <p className="text-center text-gray-500 text-xs mt-2">
        {connectionState === CONNECTION_STATES.CONNECTING
          ? 'Connecting to calling service...'
          : 'Click retry to reconnect'}
      </p>
    )}
  </>
);

export { DIALPAD_BUTTONS };
